import { useAuth0 } from "@auth0/auth0-react";
import { useState } from "react";
import { Button } from "../../../components/Button/Button.tsx";
import { Dialog } from "../../../components/Dialog/Dialog.tsx";
import { Spinner } from "../../../components/Spinner/Spinner.tsx";
import { TextField } from "../../../components/TextField/TextField.tsx";
import { useAppMutation } from "../../../http/useAppMutation.ts";
import { useUser } from "../../hooks/useUser.ts";

export const DeleteAccountDialog = ({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => (
  <Dialog
    title="Delete your entire account permanently?"
    isOpen={isOpen}
    onOpenChange={onOpenChange}
    content={<DeleteAccountContent onOpenChange={onOpenChange} />}
  />
);

const DeleteAccountContent = ({
  onOpenChange,
}: {
  onOpenChange: (open: boolean) => void;
}) => {
  const { user } = useUser();
  const { logout } = useAuth0();
  const [providedEmail, setProvidedEmail] = useState("");

  const deleteUser = useAppMutation({
    path: "users/delete",
    invalidate: [],
    onSuccess: () => {
      logout({ returnTo: window.location.origin });
    },
  }).mutation;

  if (!user) return <Spinner />;

  return (
    <div className="w-[432px] flex-col">
      <div className="py-100 px-400 flex-col gap-300">
        <div className="flex-col gap-400">
          <div className="body-md-default text-secondary">
            ⚠️ This action cannot be undone.
            <br />
            This will permanently delete your entire account, including
            workspaces, custom models and generated images.
          </div>
        </div>

        <div className="flex-col gap-100">
          <div className="body-md-semibold text-primary">
            Please type in your email to confirm.
          </div>
          <TextField
            size="md"
            placeholder={user.email}
            value={providedEmail}
            onChange={setProvidedEmail}
          />
        </div>
      </div>
      <div className="flex-row px-400 py-300 justify-end gap-200">
        <Button
          variant="secondary"
          onClick={() => onOpenChange(false)}
          size="md"
        >
          Cancel
        </Button>
        <Button
          variant="error"
          disabled={!providedEmail || providedEmail !== user.email}
          onClick={() => {
            deleteUser.mutate({});
            onOpenChange(false);
          }}
          size="md"
          className="fill-error-active"
        >
          Permanently delete my account
        </Button>
      </div>
    </div>
  );
};
